import { HOST_URL } from "config";

/**
 * 코치 웹 세션(JWT).
 *
 * 코치는 레거시 X-Auth-Token(만료 없음, localStorage["user"].accessToken) 대신 /user/v2 JWT 를 쓴다:
 * - 액세스 토큰(5분): 이 모듈 메모리에만 둔다. localStorage 에 저장하지 않는다 (XSS 노출면 제거).
 * - 리프레시 토큰(코치 5일): 서버가 코치 화면 전용 `_distancing_coach` HttpOnly 쿠키로 준다. JS 가 읽을 수 없다.
 *   (이 표식이 붙기 전에 로그인한 코치 프로필은 기존 `_distancing` 쿠키를 그대로 쓴다.)
 * - 새로고침·만료 시 refreshCoachToken() 이 쿠키로 조용히 재발급한다. 쿠키까지 만료되면 재로그인.
 *
 * 유저 웹·앱은 그대로 레거시 경로다. 어드민(adminApi.ts)이 같은 구조를 먼저 쓰고 있다.
 * 여기서는 axios 대신 fetch 를 쓴다 — api/instance 와의 순환 import 를 피하고, Jest(CRA)가 axios ESM 을
 * 변환하지 못해 이 모듈을 import 하는 테스트가 깨지는 것을 막기 위해서다.
 */
let coachToken: string | null = null;
let refreshing: Promise<string | null> | null = null;

/** userAtom 에 남는 표식. 토큰 대신 "JWT 세션으로 로그인한 사용자"임을 알린다. */
export const COACH_AUTH_MODE = "jwt" as const;

export const setCoachToken = (token: string | null) => {
  coachToken = token;
};
export const getCoachToken = () => coachToken;
export const hasCoachSession = () => !!coachToken;

/**
 * 코치 화면 세션 종류. 로그인·재발급·로그아웃에 kind 로 보내면 서버가 전용 쿠키(_distancing_coach)를 쓴다 —
 * 같은 브라우저의 프로모션 퍼널(기본 쿠키 _distancing)·어드민 페이지(_distancing_admin) 로그인이 코치 세션을 덮어쓰지 못하게 한다.
 */
export const COACH_SESSION_KIND = "coach" as const;

/**
 * 저장된 프로필의 세션 종류. 유저 PC 웹은 "pcweb"(전용 쿠키 _distancing_pc), 코치는 "coach"(_distancing_coach),
 * 표식 없는 예전 코치 프로필은 null(기본 쿠키 _distancing).
 * 서버 재발급·로그아웃에 kind 로 전달해 어느 쿠키를 다룰지 정한다.
 */
export const storedSessionKind = (): "pcweb" | "coach" | null => {
  try {
    const raw = localStorage.getItem("user");
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.sessionKind === "pcweb") return "pcweb";
    if (parsed.sessionKind === COACH_SESSION_KIND) return COACH_SESSION_KIND;
    return null;
  } catch {
    return null;
  }
};
const kindQuery = () => {
  const kind = storedSessionKind();
  return kind ? `?kind=${kind}` : "";
};

/** 리프레시 쿠키로 액세스 토큰 재발급. 동시 호출은 하나로 합친다. 실패하면 null 이고 세션은 비운다. */
export const refreshCoachToken = (): Promise<string | null> => {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const response = await fetch(
        `${HOST_URL}/user/v2/token/refresh${kindQuery()}`,
        {
          credentials: "include",
        }
      );
      const body = (await response.json()) as {
        code?: number;
        result?: { accessToken?: string };
      };
      const token = body?.result?.accessToken;
      if (response.ok && body?.code === 0 && token) {
        coachToken = token;
        return token;
      }
    } catch {
      // 쿠키 없음·만료·탈퇴 — 재로그인 필요
    }
    coachToken = null;
    return null;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
};

/** 리프레시 쿠키 삭제 + 메모리 토큰 정리. 액세스 토큰은 5분 뒤 스스로 만료된다. */
export const logoutCoach = async () => {
  try {
    await fetch(`${HOST_URL}/user/v2/logout${kindQuery()}`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // 서버가 안 받아도 로컬 세션은 비운다
  }
  coachToken = null;
};

/**
 * "마지막으로 코치로 로그인한 브라우저" 표식. 세션이 끊겨 프로필이 지워진 뒤에도 남아,
 * 자동 로그아웃·미로그인 진입 시 유저 /login 이 아니라 /coach 로 보내기 위한 것이다. 토큰·개인정보는 없다.
 */
const COACH_HINT_KEY = "coach_login_hint";
export const markCoachBrowser = () => {
  try {
    localStorage.setItem(COACH_HINT_KEY, "1");
  } catch {
    /* 저장 불가 환경이면 무시 */
  }
};
export const isCoachBrowser = (): boolean => {
  try {
    return localStorage.getItem(COACH_HINT_KEY) === "1";
  } catch {
    return false;
  }
};
/** 유저가 로그인하면 표식을 지운다 — 같은 브라우저에서 유저 계정을 쓰는 경우(코치 테스트 등) 유저 경로를 지킨다. */
export const clearCoachBrowser = () => {
  try {
    localStorage.removeItem(COACH_HINT_KEY);
  } catch {
    /* 무시 */
  }
};

/** 저장된 프로필이 JWT 세션(authMode "jwt")인지 — 메모리 토큰이 아직 복원되기 전에도 참이다. */
export const isStoredJwtSession = (): boolean => {
  try {
    const raw = localStorage.getItem("user");
    const parsed = raw ? JSON.parse(raw) : null;
    return (
      !!parsed &&
      typeof parsed === "object" &&
      parsed.authMode === COACH_AUTH_MODE
    );
  } catch {
    return false;
  }
};

/** localStorage 의 프로필 역할. 깨진 값이면 null. */
export const readStoredUserRole = (): string | null => {
  try {
    const raw = localStorage.getItem("user");
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed &&
      typeof parsed === "object" &&
      typeof parsed.role === "string"
      ? parsed.role
      : null;
  } catch {
    return null;
  }
};

/**
 * 미로그인 상태에서 보낼 로그인 화면.
 * 프로필 역할이 있으면 그것이 우선이다 — 유저 프로필이면 표식이 있어도 /login. 역할을 모를 때만
 * 코치 JWT 세션·코치 브라우저 표식으로 /coach 를 고른다. 유저 경로는 이 함수로 바뀌지 않는다.
 */
export const loginPathFor = (user?: { role?: string } | null): string => {
  const role = user?.role ?? readStoredUserRole();
  if (role === "patient") return "/login";
  if (role === "coach" || role === "admin") return "/coach";
  return hasCoachSession() || isCoachBrowser() ? "/coach" : "/login";
};

/** 로그인 여부. 레거시(토큰 보관) 또는 코치 JWT 세션. `!!user?.accessToken` 대신 이걸 쓴다. */
export const isLoggedIn = (
  user?: { accessToken?: string; authMode?: string } | null
): boolean => !!user?.accessToken || user?.authMode === COACH_AUTH_MODE;
